import { styled } from '@linaria/react'
import React, { type FC, useRef, type ChangeEvent, useState } from 'react'
import { defineMessages, FormattedMessage } from 'react-intl'
import {
  type ImageUploadErrorCode,
  useImageUpload,
} from '../hooks/useImageUpload'
import { ButtonRegular } from './Button'
import { Typography } from './Typography'
import { Loader } from './Loader'
import { colors } from '../styles/colors'
import PlusIcon from '../svgr/Plus'
import Close from '../svgr/Close'

export const ImageUploader: FC<{
  onUploadSuccess: (url: string) => void
  folder?: string
  className?: string
}> = ({ onUploadSuccess, folder = 'images', className }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { upload, isUploading, errorCode, uploadedUrl, reset } =
    useImageUpload()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }

    try {
      const url = await upload(file, { folder })
      setPreviewUrl(url)
      onUploadSuccess(url)
    } catch {
      // Error is handled by the hook
      setPreviewUrl(null)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleRemove = () => {
    setPreviewUrl(null)
    reset()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Container className={className}>
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        disabled={isUploading}
      />

      {errorCode && (
        <ErrorMessage fontSize={14} fontWeight={400}>
          <FormattedMessage {...errorMessages[errorCode]} />
        </ErrorMessage>
      )}

      {isUploading ? (
        <PreviewContainer>
          <LoaderOverlay>
            <Loader />
          </LoaderOverlay>
        </PreviewContainer>
      ) : previewUrl || uploadedUrl ? (
        <PreviewContainer>
          <PreviewImage src={previewUrl || uploadedUrl || ''} alt="" />
          <RemoveButton onClick={handleRemove}>
            <Close />
          </RemoveButton>
        </PreviewContainer>
      ) : (
        <>
          <UploadButton onClick={handleButtonClick} disabled={isUploading}>
            <UploadButtonContent>
              <PlusIconStyled />
              <Typography fontSize={16} fontWeight={400}>
                <FormattedMessage
                  id="imageUploader.uploadButton"
                  defaultMessage="Добавить"
                />
              </Typography>
            </UploadButtonContent>
          </UploadButton>
          <FileInfoText fontSize={12}>
            <FormattedMessage
              id="imageUploader.fileInfo"
              defaultMessage="JPEG, PNG, WebP, GIF. Максимум 5 МБ"
            />
          </FileInfoText>
        </>
      )}
    </Container>
  )
}

const errorMessages = defineMessages<ImageUploadErrorCode>({
  FILE_TOO_LARGE: {
    id: 'imageUploader.error.fileTooLarge',
    defaultMessage: 'Файл слишком большой. Максимум 5 МБ',
  },
  INVALID_FILE_TYPE: {
    id: 'imageUploader.error.invalidFileType',
    defaultMessage: 'Неверный формат файла',
  },
  UPLOAD_NETWORK_ERROR: {
    id: 'imageUploader.error.network',
    defaultMessage: 'Ошибка сети',
  },
  UPLOAD_HTTP_ERROR: {
    id: 'imageUploader.error.server',
    defaultMessage: 'Ошибка сервера',
  },
  UPLOAD_ERROR: {
    id: 'imageUploader.error.generic',
    defaultMessage: 'Ошибка загрузки',
  },
})

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const HiddenInput = styled.input`
  display: none;
`

const ErrorMessage = styled(Typography)`
  color: ${colors.danger};
`

const UploadButton = styled(ButtonRegular)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.secondary};
  height: 32px;
  padding: 8px 12px;
`
const UploadButtonContent = styled.p`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  gap: 8px;
`

const PlusIconStyled = styled(PlusIcon)`
  width: 20px;
  height: 20px;
`

const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 240px;
  border-radius: 16px;
  overflow: hidden;
`

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const RemoveButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${colors.white};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  box-shadow: 0 2px 12px ${colors.shadowMedium};

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 20px;
    height: 20px;

    path {
      fill: ${colors.textPrimary};
    }
  }
`

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.shadowDark};
  border-radius: 16px;
`

const FileInfoText = styled(Typography)`
  color: ${colors.textSecondary};
  text-align: center;
  margin-top: -6px;
`
